import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { StorageService } from '../../../core/storage/storageService';
import { TransactionFacade } from '../../../models/transactions/transactionFacade';
import { TransactionService } from '../../../models/transactions/transactionService';
import { Router } from '@angular/router';
import { OrderService } from '../../../models/orders/orderService';
import { CustomSnackbarComponent } from '../../ui/snackbar/custom-snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UIErrorHandler } from '../../../core/uiErrorHandler';
import { UIHelpers } from '../../../core/ui-helpers';
import { AccessManager } from '../../ui/access-manager/access-manager';

@Component({
  selector: 'app-transaction-dialog',
  templateUrl: './transaction-dialog.component.html',
  standalone: false,
})
export class TransactionDialogComponent {
  formattedTransactionDate: string = '';
  canCancelRecurringPayment: boolean = false;
  sameDayTransaction: boolean = false;
  disableCancelRecurringPayment: boolean = false;
  loading: boolean = false;
  _transaction: TransactionFacade | undefined;
  sendingReceipt: boolean = false;
  transactionError: string = '';
  actionClicked: boolean = false;
  hasBeenPartiallyRefunded: boolean = false;
  partiallyRefundedAmount: string = '';
  actionTitle: string = '';
  actionMessage: string = '';
  canManageTransaction: boolean = false;

  constructor(
    private transactionService: TransactionService,
    private router: Router,
    private orderService: OrderService,
    private snackBar: MatSnackBar,
    private uiErrorHandler: UIErrorHandler,
    private uiHelpers: UIHelpers,
    public dialogRef: MatDialogRef<TransactionDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { transaction: TransactionFacade },
    private access: AccessManager
  ) {}

  ngOnInit() {
    this.transactionService.getTransaction(this.data.transaction.id).then((transaction) => {
      this._transaction = transaction;
      this.formattedTransactionDate = new Date(this._transaction?.date!).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
    });
     this.access.getUserAccess([
      AccessManager.AUTH_INSERT_MERCHANT_TRANSACTIONS,
      AccessManager.AUTH_MANAGE_ALL_TRANSACTIONS,
    ]).then((access) => {
      this.canManageTransaction = access[AccessManager.AUTH_INSERT_MERCHANT_TRANSACTIONS] || access[AccessManager.AUTH_MANAGE_ALL_TRANSACTIONS];
    });
    const today = new Date().toISOString().split('T')[0];
    if (this._transaction?.date?.split('T')[0] == today) {
      this.sameDayTransaction = true;
    } else {
      this.sameDayTransaction = false;
    }
    if (
      this._transaction?.type == 'RECURRING' ||
      this._transaction?.type == 'VERIFY'
    ) {
      this.canCancelRecurringPayment = true;
    }
  }

  close_click() {
    this.dialogRef.close();
  }

  onSaleCapture() {
    this.dialogRef.close(true);
    if (this._transaction) {
      this.router.navigate([
        'merchants/terminal',
        'tid',
        this._transaction.id,
        'v',
        '2',
      ]);
    }
  }

  onContinueClicked() {
    switch (this.actionTitle ) {
      case 'Begin Sale Return':
        this.onSaleReturn();
        break;
      case 'Begin Sale Void':
        this.onSaleVoid();
        break;
      case 'Begin Sale Capture':
        this.onSaleCapture();
        break;
    }
  }

  onActionButtonClicked(action: string) {
    this.actionClicked = true;
    switch (action) {
      case 'saleReturn':
        this.actionTitle = 'Begin Sale Return';
        this.actionMessage = 'refund';
        if (this._transaction?.maxRefundAmount! != this._transaction?.magnifiedAmount!) {
          this.hasBeenPartiallyRefunded = true;
          this.partiallyRefundedAmount = this.uiHelpers.formatCurrencyToString(
            this.uiHelpers.demagnifyAmount(
              this._transaction?.magnifiedAmount! -
              this._transaction?.maxRefundAmount!,
            this._transaction?.magnifier!,
            ),
          );
        } else {
          this.hasBeenPartiallyRefunded = false;
          this.partiallyRefundedAmount = "";
        }
        break;
      case 'saleVoid':
        this.actionTitle = 'Begin Sale Void';
        this.actionMessage = 'void';
        break;
      case 'void':
        this.actionTitle = 'Begin Sale Void';
        this.actionMessage = 'void';
        break;
      case 'capture':
        this.actionTitle = 'Begin Sale Capture';
        this.actionMessage = 'capture the remaining amount';
        break;
      case 'cancelRecurringPayment':
        this.cancelRecurringPayment();
        break;
    }
  }

  onSaleReturn() {
    this.dialogRef.close(true);
    if (this._transaction) {
      this.router.navigate([
        'merchants/terminal',
        'tid',
        this._transaction.id,
        'v',
        '4',
      ]);
    }
  }

  onSaleVoid() {
    this.dialogRef.close(true);
    if (this._transaction) {
      this.router.navigate([
        'merchants/terminal',
        'tid',
        this._transaction.id,
        'v',
        '3',
      ]);
    }
  }

  sendTransactionToCustomer() {
    this.loading = true;
    this.sendingReceipt = true;
    this.orderService
      .getOrder(this._transaction?.orderId!)
      .then((order) => {
        this.transactionService
          .sendTransactionToCustomer(
            order.recipientEmail!,
            order,
            this._transaction?.type!,
          )
          .then(() => {
            this.loading = false;
          })
          .catch((error) => {
            this.loading = false;
            this.transactionError = error.message;
          });
      })
      .catch((error) => {
        this.loading = false;
        this.uiErrorHandler.handleError(error);
      });
  }

  cancelRecurringPayment() {
    this.disableCancelRecurringPayment = true;
    this.loading = true;
    this.transactionService
      .cancelRecurringPayment(
        this._transaction?.orderId!,
        this._transaction?.type!,
      )
      .then(() => {
        this.loading = false;
        this.snackBar?.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-success',
          data: {
            message: 'Recurring payment has been cancelled.',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      })
      .catch((error) => {
        this.loading = false;
        this.disableCancelRecurringPayment = false;
        this.snackBar?.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-error',
          data: {
            message:
              'There was a problem canceling the recurring payment: ' + error,
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      });
  }
}
